import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import {
  resolveSeriesTotalImages,
  countDownloadedSeriesImages,
  resolveFileNumberOfFrames,
} from '../../utils/seriesImageCounts'
import {
  resolveExplorerThumbnailPreviewUrl,
  buildExplorerNonImageSlots,
  findOrdinalPairedDisplayableIndex,
} from '../../utils/explorerThumbnailPolicy'
import {
  isNonImageFileItem,
  explorerNonImageBadge,
  buildNonImageThumbnailDataUrl,
  fileItemModality,
} from '../../utils/isNonImageInstance'
import {
  buildInstanceSquares,
  isSeriesInstanceReady,
  MAX_VISIBLE_SQUARES,
} from '../../utils/oviyamInstanceGrid'
import {
  buildStudySeriesMaps,
  buildSeriesMapForStudy,
  studyUidFromFile,
} from '../../utils/comparePriorStudy'
import { t } from '../../i18n'

function isInstanceReady(f) {
  if (!f || f.error) return false
  if (f.skeleton === true) return false
  return !!(f.imageId || f.image || resolveExplorerThumbnailPreviewUrl(f))
}

function sortSeriesFiles(files) {
  if (!Array.isArray(files) || !files.length) return []
  return files.slice().sort((a, b) => {
    const ia =
      a && a.instanceNumber != null ? Number(a.instanceNumber) : Number.MAX_SAFE_INTEGER
    const ib =
      b && b.instanceNumber != null ? Number(b.instanceNumber) : Number.MAX_SAFE_INTEGER
    if (ia !== ib) return ia - ib
    const sa = a && a.sliceLocation != null ? Number(a.sliceLocation) : 0
    const sb = b && b.sliceLocation != null ? Number(b.sliceLocation) : 0
    return sa - sb
  })
}

function seriesModality(files) {
  const f = files && files[0]
  if (!f) return ''
  const fromMeta = fileItemModality(f)
  if (fromMeta) return fromMeta
  const badge = explorerNonImageBadge(f)
  return badge || ''
}

function seriesLabel(files) {
  const f = files && files[0]
  if (!f || !f.series) return 'Series'
  const mod = seriesModality(files)
  const desc =
    f.series.seriesDescription ||
    f.series.description ||
    ''
  const num =
    f.series.seriesNumber != null ? `S${f.series.seriesNumber}` : ''
  const parts = []
  if (num) parts.push(num)
  if (mod) parts.push(mod)
  const head = parts.join(' · ')
  if (desc && head) return `${head}: ${desc}`
  if (desc) return desc
  return head || 'Series'
}

function pickThumbSlots(sorted) {
  const list = Array.isArray(sorted) ? sorted : []
  const withPreview = list.filter((f) => resolveExplorerThumbnailPreviewUrl(f))
  const pool = withPreview.length ? withPreview : list
  if (!pool.length) return []
  if (pool.length === 1) return [pool[0]]
  if (pool.length === 2) return [pool[0], pool[pool.length - 1]]
  const mid = Math.floor((pool.length - 1) / 2)
  return [pool[0], pool[mid], pool[pool.length - 1]]
}

function firstFileOf(files) {
  const sorted = sortSeriesFiles(files)
  return sorted[0] || null
}

function seriesNeedsProgressPoll(files) {
  if (!Array.isArray(files) || !files.length) return false
  for (let i = 0; i < files.length; i++) {
    const f = files[i]
    if (isNonImageFileItem(f)) continue
    if (!isInstanceReady(f)) return true
    const nf = resolveFileNumberOfFrames(f)
    if (nf > 1) {
      const readyN =
        f.framesReadySet && typeof f.framesReadySet.size === 'number'
          ? f.framesReadySet.size
          : Number(f.framesReadyCount) || 0
      if (readyN < nf) return true
    }
  }
  return false
}

/**
 * Thumbs para serie imagen (1ª/media/última) o PR/SR via pareo XA (como Explorer/Studio).
 * @returns {Array<{ src: string, ready: boolean, badge?: string }>}
 */
function resolveSeriesThumbs(entry, allEntries, slots) {
  const first = firstFileOf(entry.files)
  if (!first) return []

  if (isNonImageFileItem(first)) {
    const badge = explorerNonImageBadge(first) || 'PR'
    const placeholder = buildNonImageThumbnailDataUrl(badge)
    const pairedIdx = findOrdinalPairedDisplayableIndex(slots, entry.index)
    let src = placeholder
    let ready = false
    if (pairedIdx != null && allEntries[pairedIdx]) {
      const pairFirst = firstFileOf(allEntries[pairedIdx].files)
      const preview = resolveExplorerThumbnailPreviewUrl(pairFirst)
      if (preview) {
        src = preview
        ready = true
      } else if (pairFirst && isInstanceReady(pairFirst)) {
        ready = true
      }
    }
    return [{ src, ready, badge }]
  }

  const slotsFiles = pickThumbSlots(
    entry.files.filter((f) => !isNonImageFileItem(f))
  )
  if (!slotsFiles.length) {
    const badge = explorerNonImageBadge(first) || 'IMG'
    return [
      {
        src: buildNonImageThumbnailDataUrl(badge),
        ready: false,
        badge,
      },
    ]
  }
  return slotsFiles.map((f) => {
    const preview = resolveExplorerThumbnailPreviewUrl(f)
    const ready = isInstanceReady(f)
    return {
      src: preview || '',
      ready,
    }
  })
}

/**
 * West pane Oviyam: accordion por estudio + series (UID-safe, como Studio Explorer).
 */
function OviyamNgSeriesPane({
  files,
  onSelectSeries,
  autoSelectFirstSeries,
  explorerActiveSeriesIndex,
  activeSliceIndex = 0,
  collapsedStudyKeys = [],
  onToggleStudyCollapse,
  viewportStudyUids = [],
}) {
  const [expanded, setExpanded] = useState({})
  const [tick, setTick] = useState(0)
  const [liveSlice, setLiveSlice] = useState(
    () => Math.max(0, Number(activeSliceIndex) || 0)
  )
  const autoDoneRef = useRef(false)
  const prevFilesLen = useRef(0)
  const sliceRafRef = useRef(null)
  const pendingSliceRef = useRef(null)

  useEffect(() => {
    setLiveSlice(Math.max(0, Number(activeSliceIndex) || 0))
  }, [activeSliceIndex])

  // Scroll en vivo sin setState de App (misma fluidez que Studio).
  useEffect(() => {
    const onNav = (e) => {
      const i = e && e.detail ? Number(e.detail.index) : NaN
      if (!Number.isFinite(i) || i < 0) return
      pendingSliceRef.current = i
      if (sliceRafRef.current != null) return
      sliceRafRef.current = requestAnimationFrame(() => {
        sliceRafRef.current = null
        const next = pendingSliceRef.current
        if (next != null) setLiveSlice(next)
      })
    }
    window.addEventListener('udv-nav-slice', onNav)
    return () => {
      window.removeEventListener('udv-nav-slice', onNav)
      if (sliceRafRef.current != null) {
        cancelAnimationFrame(sliceRafRef.current)
        sliceRafRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const id = setInterval(() => {
      const list = Array.isArray(files) ? files : []
      if (!list.length) return
      if (seriesNeedsProgressPoll(list)) setTick((n) => n + 1)
    }, 250)
    return () => clearInterval(id)
  }, [files])

  const studyGroups = useMemo(() => {
    const list = Array.isArray(files) ? files.filter(Boolean) : []
    if (!list.length) return []
    const { studyList, studyKeys, studyLabels } = buildStudySeriesMaps(list)
    let globalIndex = 0
    const groups = []
    const allEntries = []
    for (let si = 0; si < studyKeys.length; si++) {
      const studyKey = studyKeys[si]
      const studyFiles = studyList.get(studyKey) || []
      const { seriesList, seriesKeys } = buildSeriesMapForStudy(studyFiles)
      const studyUid = studyUidFromFile(studyFiles[0]) || ''
      const entries = []
      for (let i = 0; i < seriesKeys.length; i++) {
        const key = seriesKeys[i]
        const raw = seriesList.get(key) || []
        const sorted = sortSeriesFiles(raw)
        const total = Math.max(
          1,
          resolveSeriesTotalImages(sorted) || sorted.length
        )
        const ready = countDownloadedSeriesImages(sorted, isInstanceReady)
        const first = sorted[0]
        const entry = {
          key: `${studyKey}::${key}`,
          seriesKey: key,
          index: globalIndex,
          files: sorted,
          label: seriesLabel(sorted),
          modality: seriesModality(sorted),
          total,
          ready,
          nonImage: !!(first && isNonImageFileItem(first)),
        }
        entries.push(entry)
        allEntries.push(entry)
        globalIndex += 1
      }
      groups.push({
        studyKey,
        studyUid,
        label: studyLabels[studyKey] || studyKey,
        seriesCount: entries.length,
        instanceCount: studyFiles.length,
        entries,
      })
    }
    const slots = buildExplorerNonImageSlots(
      allEntries.map((e) => e.key),
      (k) => {
        const hit = allEntries.find((e) => e.key === k)
        return firstFileOf(hit && hit.files)
      }
    )
    // Slots usan índice posicional 0..n; alinear con entry.index (global)
    const slotsByGlobal = slots.map((s) => ({
      ...s,
      index:
        typeof s.index === 'number' && allEntries[s.index]
          ? allEntries[s.index].index
          : s.index,
    }))
    for (let g = 0; g < groups.length; g++) {
      groups[g].entries = groups[g].entries.map((entry) => ({
        ...entry,
        thumbs: resolveSeriesThumbs(entry, allEntries, slotsByGlobal),
      }))
    }
    return groups
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, tick])

  const seriesEntries = useMemo(() => {
    const flat = []
    for (let i = 0; i < studyGroups.length; i++) {
      const g = studyGroups[i]
      for (let j = 0; j < g.entries.length; j++) flat.push(g.entries[j])
    }
    return flat
  }, [studyGroups])

  const vpUidSet = useMemo(() => {
    const set = new Set()
    const raw = Array.isArray(viewportStudyUids) ? viewportStudyUids : []
    for (let i = 0; i < raw.length; i++) {
      const u = raw[i] != null ? String(raw[i]).trim() : ''
      if (u) set.add(u)
    }
    return set
  }, [viewportStudyUids])

  const collapsedSet = useMemo(() => {
    const set = new Set()
    const raw = Array.isArray(collapsedStudyKeys) ? collapsedStudyKeys : []
    for (let i = 0; i < raw.length; i++) {
      if (raw[i] != null) set.add(String(raw[i]))
    }
    return set
  }, [collapsedStudyKeys])

  const multiStudy = studyGroups.length > 1

  const activeIndex =
    typeof explorerActiveSeriesIndex === 'number' && explorerActiveSeriesIndex >= 0
      ? explorerActiveSeriesIndex
      : 0

  const sliceIdx =
    typeof liveSlice === 'number' && Number.isFinite(liveSlice)
      ? Math.max(0, Math.floor(liveSlice))
      : 0

  useEffect(() => {
    if (!seriesEntries.length) {
      autoDoneRef.current = false
      prevFilesLen.current = 0
      return
    }
    setExpanded((prev) => {
      const next = { ...prev }
      let changed = false
      seriesEntries.forEach((s) => {
        if (next[s.key] === undefined) {
          next[s.key] = true
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [seriesEntries, activeIndex])

  useEffect(() => {
    if (!autoSelectFirstSeries || !onSelectSeries || !seriesEntries.length) return
    const len = Array.isArray(files) ? files.length : 0
    if (autoDoneRef.current && len <= prevFilesLen.current) {
      prevFilesLen.current = len
      return
    }
    prevFilesLen.current = len
    const firstDisplay =
      seriesEntries.find((s) => !s.nonImage && s.files.length) || seriesEntries[0]
    if (!firstDisplay || !firstDisplay.files.length) return
    autoDoneRef.current = true
    try {
      onSelectSeries(firstDisplay.files, firstDisplay.index)
    } catch (e) {
      /* ignore */
    }
  }, [autoSelectFirstSeries, onSelectSeries, seriesEntries, files])

  const toggleExpand = useCallback((key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const toggleStudy = useCallback(
    (studyKey) => {
      if (typeof onToggleStudyCollapse === 'function') {
        onToggleStudyCollapse(studyKey)
      }
    },
    [onToggleStudyCollapse]
  )

  const handleSelect = useCallback(
    (entry) => {
      if (!onSelectSeries || !entry || !entry.files.length) return
      // PR/GSPS/SR: abrir XA (u otra serie imagen) pareada + GSPS aplica presets.
      if (entry.nonImage) {
        const slots = buildExplorerNonImageSlots(
          seriesEntries.map((e) => e.key),
          (k) => {
            const hit = seriesEntries.find((e) => e.key === k)
            return firstFileOf(hit && hit.files)
          }
        )
        const pairedIdx = findOrdinalPairedDisplayableIndex(slots, entry.index)
        if (pairedIdx != null && pairedIdx !== entry.index) {
          const paired = seriesEntries[pairedIdx]
          if (paired && paired.files && paired.files.length) {
            onSelectSeries(paired.files, paired.index)
            return
          }
        }
        // Fallback: SOP referenciado en el GSPS
        const prFile = entry.files[0]
        ;(async () => {
          try {
            const { parseGspsFromFile } = await import('../../utils/gspsIndex')
            const parsed = await parseGspsFromFile(prFile)
            const uids = (parsed && parsed.referencedSopUids) || []
            if (!uids.length) return
            const want = new Set(uids.map((u) => String(u)))
            for (let i = 0; i < seriesEntries.length; i++) {
              const s = seriesEntries[i]
              if (!s || s.index === entry.index || s.nonImage) continue
              const hit = (s.files || []).some((f) => {
                if (!f) return false
                const sop =
                  f.sopInstanceUID ||
                  f.SOPInstanceUID ||
                  (f.series &&
                    (f.series.sopInstanceUID || f.series.SOPInstanceUID)) ||
                  ''
                return sop && want.has(String(sop))
              })
              if (!hit) continue
              onSelectSeries(s.files, s.index)
              return
            }
          } catch (e) {
            /* ignore */
          }
        })()
        return
      }
      onSelectSeries(entry.files, entry.index)
    },
    [onSelectSeries, seriesEntries]
  )

  const renderSeriesCard = (entry) => {
    const isOpen = expanded[entry.key] !== false
    const isActive = entry.index === activeIndex
    const squares = entry.nonImage
      ? []
      : buildInstanceSquares({
          total: entry.total,
          currentIndex: sliceIdx,
          isActiveSeries: isActive,
          isReadyAt: (i) =>
            isSeriesInstanceReady(
              entry.files,
              i,
              isInstanceReady,
              resolveFileNumberOfFrames
            ),
        })
    const binned = entry.total > MAX_VISIBLE_SQUARES
    return (
      <div
        key={entry.key}
        className={
          'udv-oviyamng-series-card' +
          (isActive ? ' is-active' : '') +
          (entry.nonImage ? ' is-nonimage' : '')
        }
        role="listitem"
      >
        <button
          type="button"
          className="udv-oviyamng-series-head"
          onClick={() => {
            toggleExpand(entry.key)
            handleSelect(entry)
          }}
        >
          <span className="udv-oviyamng-series-head-label" title={entry.label}>
            {entry.label}
          </span>
          {entry.modality ? (
            <span
              className={
                'udv-oviyamng-series-mod' +
                (entry.nonImage ? ' is-nonimage' : '')
              }
              title={`Modalidad ${entry.modality}`}
            >
              {entry.modality}
            </span>
          ) : null}
          <span className="udv-oviyamng-series-head-count">
            {entry.ready}/{entry.total}
          </span>
        </button>
        {isOpen ? (
          <div className="udv-oviyamng-series-body-inner">
            {!entry.nonImage && squares.length ? (
              <div
                className="udv-oviyamng-inst-grid"
                title={
                  binned
                    ? `${entry.ready}/${entry.total} · ${squares.length} bins (máx ${MAX_VISIBLE_SQUARES})`
                    : `${entry.ready} de ${entry.total} instancias`
                }
                aria-hidden="true"
              >
                {squares.map((sq, i) => {
                  const cls = [
                    'udv-oviyamng-inst-sq',
                    sq.ready ? 'is-ready' : 'is-pending',
                    sq.current ? 'is-current' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')
                  return <span key={i} className={cls} />
                })}
                {binned ? (
                  <span className="udv-oviyamng-inst-more" title="Agrupados">
                    ×{entry.total}
                  </span>
                ) : null}
              </div>
            ) : null}
            <div className="udv-oviyamng-thumbs-row">
              {entry.thumbs.map((thumb, ti) => (
                <button
                  key={ti}
                  type="button"
                  className={
                    'udv-oviyamng-thumb' +
                    (thumb.ready ? ' is-ready' : '') +
                    (thumb.badge ? ' is-badge' : '')
                  }
                  onClick={() => handleSelect(entry)}
                  title={
                    thumb.badge
                      ? `${entry.label} (${thumb.badge})`
                      : entry.label
                  }
                >
                  {thumb.src ? (
                    <img src={thumb.src} alt="" draggable={false} />
                  ) : (
                    <span className="udv-oviyamng-thumb-ph">
                      {thumb.ready ? '…' : '·'}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  if (!seriesEntries.length) {
    return <div className="udv-oviyamng-series-empty">Sin series</div>
  }

  return (
    <div className="udv-oviyamng-series-pane" role="list">
      {studyGroups.map((group) => {
        const studyCollapsed =
          multiStudy && collapsedSet.has(String(group.studyKey))
        const inViewport = !!(
          group.studyUid && vpUidSet.has(String(group.studyUid))
        )
        if (!multiStudy) {
          return (
            <React.Fragment key={group.studyKey}>
              {group.entries.map((entry) => renderSeriesCard(entry))}
            </React.Fragment>
          )
        }
        return (
          <div
            key={group.studyKey}
            className={
              'udv-oviyamng-study-sec' + (inViewport ? ' is-in-viewport' : '')
            }
          >
            <button
              type="button"
              className="udv-oviyamng-study-sec-head"
              onClick={() => toggleStudy(group.studyKey)}
              title={group.label}
              aria-expanded={!studyCollapsed}
            >
              <span aria-hidden="true" className="udv-oviyamng-study-sec-chevron">
                {studyCollapsed ? '▸' : '▾'}
              </span>
              <span className="udv-oviyamng-study-sec-label">{group.label}</span>
              <span className="udv-oviyamng-study-sec-meta">
                {group.seriesCount} ser.
              </span>
              {inViewport ? (
                <span className="udv-oviyamng-study-sec-vp">
                  {t('relatedStudies.inViewport')}
                </span>
              ) : null}
            </button>
            {!studyCollapsed
              ? group.entries.map((entry) => renderSeriesCard(entry))
              : null}
          </div>
        )
      })}
    </div>
  )
}

const mapStateToProps = (state) => ({
  files: state.files,
  explorerActiveSeriesIndex: state.explorerActiveSeriesIndex,
})

export default connect(mapStateToProps)(OviyamNgSeriesPane)
